'use client'
import { NavRightArrow } from '@/svgs'
import DragDropIcon from '@/svgs/DragDropIcon'
import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toastError, toastSuccess } from '@/utils/toaster'
// Update the imports at the top
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'

const allowedFileTypes = ['.pdf']
const maxFileSize = 5 * 1024 * 1024

function ApplyJobForm({ slug }) {
   const fileInputRef = useRef(null)

   const [isLoading, setIsLoading] = useState(false)
   const [phoneNumber, setPhoneNumber] = useState()

   const [isDrag, setIsDrag] = useState(false)
   const {
      register,
      handleSubmit,
      setValue,
      getValues,
      watch,
      formState: { errors },
      reset,
      setError,
      clearErrors,
   } = useForm()
   register('file', { required: 'File is required' })
   register('mobile_number', { required: 'Phone number is required' })

   const file = watch('file')
   // Add phone validation function
   const validatePhoneNumber = (value) => {
      if (!value) {
         setError('mobile_number', {
            type: 'manual',
            message: 'Phone number is required',
         })
         return false
      }

      if (!isValidPhoneNumber(value)) {
         setError('mobile_number', {
            type: 'manual',
            message: 'Please enter a valid phone number',
         })
         return false
      }

      clearErrors('mobile_number')
      return true
   }

   const onSubmit = async (data) => {
      if (!phoneNumber || !validatePhoneNumber(phoneNumber)) {
         return
      }
      setIsLoading(true)
      // Update to use phoneNumber state instead of itiRef
      const newData = { ...data, mobile_number: phoneNumber }

      if (!window.grecaptcha) return

      await window.grecaptcha.ready(() => {
         window.grecaptcha
            .execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY, {
               action: 'submit',
            })
            .then(async (token) => {
               const formData = new FormData()

               Object.entries(newData).forEach(([key, value]) => {
                  formData.append(key, value)
               })
               formData.append('token', token)
               // Job Inquiry: Student/JobSeeker name (Selected Technology

               formData.append('subject', `Job Inquiry: ${data?.name} (${slug})`)

               try {
                  const res = await fetch(
                     `${process.env.NEXT_PUBLIC_BACKEND_URL}/careers/applyNew`,
                     {
                        method: 'POST',
                        body: formData,
                     }
                  )

                  if (!res.ok) throw new Error('Server error')
                  const result = await res.json()
                  toastSuccess(result.msg)
                  reset()
                  setIsLoading(false)
               } catch (err) {
                  toastError(
                     'There was an error submitting the form. Please try again.'
                  )
                  setIsLoading(false)
               } finally {
                  setIsLoading(false)
               }
            })
      })
   }

   const validateAndSetFile = (file) => {
      if (!file) return

      const ext = '.' + file.name.split('.').pop().toLowerCase()
      if (!allowedFileTypes.includes(ext)) {
         setError('file', {
            type: 'manual',
            message: `Invalid file type. Allowed: ${allowedFileTypes.join(
               ', '
            )}`,
         })
         setValue('file', null)
         return
      }

      if (file.size > maxFileSize) {
         setError('file', {
            type: 'manual',
            message: 'File is too large. Maximum size is 5MB',
         })
         setValue('file', null)
         return
      }

      clearErrors('file')
      setValue('file', file)
   }

   const handleFileChange = (e) => {
      const file = e.target.files[0]
      validateAndSetFile(file)
   }

   const handleDragEvents = (e, highlight) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDrag(highlight)
   }

   const handleDrop = (e) => {
      e.preventDefault()
      const file = e.dataTransfer.files[0]
      validateAndSetFile(file)
      setIsDrag(false)
   }

   return (
      <section className="cv_form section_secondary_bg" id="apply-form">
         <div className="container">
            <div className="row">
               <div className="col-xxl-4 pe-xxl-0 mb-4 mb-xxl-0">
                  <div className="cv_form_left">
                     <h2 className="section_heading_title">
                        Apply and Schedule an Interview
                     </h2>
                     <hr />
                     <div className="contact_info">
                        <h3>Contact Information</h3>
                        <div className="info_item">
                           <svg
                              width="52"
                              height="52"
                              viewBox="0 0 52 52"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                           >
                              <circle cx="26" cy="26" r="26" fill="#F1ECDC" />
                              <mask
                                 id="mask0_5451_25587"
                                 style={{ maskType: 'luminance' }}
                                 maskUnits="userSpaceOnUse"
                                 x="12"
                                 y="12"
                                 width="27"
                                 height="27"
                              >
                                 <path
                                    d="M12.75 12.491H38.625V38.504H12.75V12.491Z"
                                    fill="white"
                                 />
                              </mask>
                              <g mask="url(#mask0_5451_25587)">
                                 <path
                                    d="M29.3221 27.879L28.7541 28.419C28.7541 28.419 27.4021 29.702 23.7141 26.199C20.0241 22.697 21.3761 21.415 21.3761 21.415L21.7331 21.074C22.6151 20.237 22.6991 18.892 21.9291 17.909L20.3561 15.902C19.4021 14.686 17.5611 14.525 16.4681 15.562L14.5081 17.422C13.9681 17.937 13.6061 18.602 13.6501 19.342C13.7621 21.234 14.6581 25.302 19.6571 30.05C24.9581 35.084 29.9331 35.284 31.9671 35.103C32.6111 35.046 33.1701 34.733 33.6211 34.304L35.3931 32.621C36.5921 31.485 36.2551 29.535 34.7221 28.741L32.3371 27.501C31.3311 26.979 30.1081 27.133 29.3221 27.879Z"
                                    fill="#1F473E"
                                 />
                              </g>
                           </svg>
                           <div>
                              <h6>call us on our HR</h6>
                              <a
                                 href="tel:+917567583566"
                                 className="cs-text-secondary"
                              >
                                 +91 75675 83566
                              </a>
                           </div>
                        </div>
                        <div className="info_item">
                           <svg
                              width="52"
                              height="52"
                              viewBox="0 0 52 52"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                           >
                              <circle cx="26" cy="26" r="26" fill="#F1ECDC" />
                              <path
                                 d="M13.7569 19.2148C13.7569 18.5078 14.0369 17.8298 14.5339 17.3308C14.7793 17.0835 15.0713 16.8873 15.3929 16.7535C15.7145 16.6196 16.0595 16.5507 16.4079 16.5508H34.9579C35.6619 16.5508 36.3359 16.8308 36.8329 17.3308C37.3299 17.8308 37.6089 18.5078 37.6089 19.2148V32.5358C37.6089 33.2428 37.3289 33.9208 36.8329 34.4198C36.3359 34.9198 35.6619 35.1998 34.9589 35.1998H16.4069C15.7039 35.1998 15.0299 34.9198 14.5329 34.4198C14.035 33.9192 13.7556 33.2418 13.7559 32.5358L13.7569 19.2148Z"
                                 fill="#1F473E"
                              />
                              <path
                                 d="M14.6875 18.0078L25.6875 25.9068L36.6875 18.0078"
                                 fill="#1F473E"
                              />
                              <path
                                 d="M14.6875 18.0078L25.6875 25.9068L36.6875 18.0078"
                                 stroke="#F1ECDC"
                                 strokeWidth="1.5"
                                 strokeLinecap="round"
                                 strokeLinejoin="round"
                              />
                           </svg>

                           <div>
                              <h6>DROP YOUR cv/RESUME AT :</h6>
                              <a
                                 href="mailto:hr@benzatine.com"
                                 className="cs-text-secondary"
                              >
                                 hr@benzatine.com
                              </a>
                           </div>
                        </div>
                     </div>
                     <div className="grow-text">
                        <hr />
                        <p>GROW • INNOVATE • SUCCEED • TOGETHER!</p>
                     </div>
                  </div>
               </div>
               <div className="col-xxl-8 ps-xxl-0">
                  <div className="cv_form_right">
                     <h3 className="tech_title mb-0">
                        KINDLY E-MAIL YOUR CV/RESUME
                     </h3>
                     <p className="note">
                        Your email address will not be published. Required
                        fields are marked with * symbol.
                     </p>
                     <div className="footer_form cv_form_part m-0">
                        <form
                           id="inquiryForm"
                           onSubmit={handleSubmit(onSubmit)}
                           encType="multipart/form-data"
                        >
                           <div className="form_row">
                              <div className="form_group">
                                 <input
                                    type="text"
                                    placeholder="Name*"
                                    {...register('name', {
                                       required: 'Name is required',
                                    })}
                                 />
                                 {errors.name && (
                                    <div className="error-message">
                                       {errors.name.message}
                                    </div>
                                 )}
                              </div>

                              <div className="form_group">
                                 <PhoneInput
                                    name="mobile_number"
                                    international
                                    countryCallingCodeEditable={true}
                                    defaultCountry="IN"
                                    value={phoneNumber}
                                    className="mobile_number_input"
                                    onChange={(value) => {
                                       setPhoneNumber(value)
                                       if (value) {
                                          validatePhoneNumber(value)
                                          setValue('mobile_number', value)
                                       }
                                    }}
                                    onBlur={() =>
                                       validatePhoneNumber(phoneNumber)
                                    }
                                    error={errors.mobile_number?.message}
                                 />
                                 {errors.mobile_number && (
                                    <div className="error-message">
                                       {errors.mobile_number.message}
                                    </div>
                                 )}
                              </div>
                           </div>

                           <div className="form_row">
                              <div className="form_group">
                                 <input
                                    type="email"
                                    placeholder="Email Address*"
                                    {...register('email', {
                                       required: 'Email is required',
                                       pattern: {
                                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                          message: 'Invalid email',
                                       },
                                    })}
                                 />
                                 {errors.email && (
                                    <div className="error-message">
                                       {errors.email.message}
                                    </div>
                                 )}
                              </div>
                              <div className="form_group">
                                 <textarea
                                    placeholder="Message*"
                                    {...register('message', {
                                       required: 'Message is required',
                                    })}
                                 ></textarea>
                                 {errors.message && (
                                    <div className="error-message">
                                       {errors.message.message}
                                    </div>
                                 )}
                              </div>
                           </div>

                           <div className="form_row"></div>

                           <div className="form_row">
                              <div className="form_group full">
                                 <div
                                    className={`file_upload ${
                                       isDrag ? 'drag-active' : ''
                                    }`}
                                    onDragEnter={(e) =>
                                       handleDragEvents(e, true)
                                    }
                                    onDragOver={(e) =>
                                       handleDragEvents(e, true)
                                    }
                                    onDragLeave={(e) =>
                                       handleDragEvents(e, false)
                                    }
                                    onDrop={handleDrop}
                                    onClick={() => fileInputRef.current.click()}
                                 >
                                    <input
                                       type="file"
                                       ref={fileInputRef}
                                       onChange={handleFileChange}
                                       accept=".pdf"
                                       style={{ display: 'none' }}
                                    />
                                    <label htmlFor="file_input">
                                       <DragDropIcon />
                                       {file?.name ||
                                          'Drag & Drop Files Here Or Click To Upload'}
                                    </label>

                                    <div className="scheduled-btn-section choose_file_btn">
                                       <button
                                          type="button"
                                          className="scheduled-btn"
                                       >
                                          Choose File
                                       </button>
                                    </div>
                                 </div>
                                 {errors.file && (
                                    <div className="error-message">
                                       {errors.file.message}
                                    </div>
                                 )}
                                 <p className="file_info fw-semibold ms-4 text-start">
                                    Upload only PDF file.
                                 </p>
                              </div>
                           </div>
                           <div className="d-flex justify-content-center">
                              <button
                                 type="submit"
                                 id="collaborate-submit"
                                 className={` submit_btn text-uppercase`}
                                 disabled={isLoading}
                              >
                                 Apply Now
                                 <NavRightArrow color={'light'} />
                              </button>
                           </div>
                        </form>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </section>
   )
}

export default ApplyJobForm
